import type { ContextStatus, DataCaptureContext } from '@scandit/web-datacapture-core';
import { SCANDIT_LICENSE_KEY } from '../../../core/config';
import isNil from '../../../core/helpers/is-nil';
import { getLoggerInstance } from '../../../core/monitoring/logger';

const PUBLIC_SDC_LIB_PATH = '/sdc-lib/';

const logger = getLoggerInstance('setup-scandit');

class NonSuccessScanditStatusError extends Error {
  public readonly code: number;
  public readonly statusMessage: string;

  constructor(code: number, statusMessage: string) {
    const name = 'NonSuccessScanditStatusError';
    super(`${name} Code: (${code}). Message: ${statusMessage}`);
    this.name = name;
    this.code = code;
    this.statusMessage = statusMessage;
  }
}

export async function setupScandit({
  container,
  onBarcodeDetect,
  reportError,
  onInit,
}: {
  container: HTMLDivElement;
  onBarcodeDetect: (onBarcodeDetect: string) => void;
  reportError: (error: unknown) => void;
  onInit: () => void;
}) {
  let SDCCore: typeof import('@scandit/web-datacapture-core');
  let SDCBarcode: typeof import('@scandit/web-datacapture-barcode');
  try {
    SDCCore = await import('@scandit/web-datacapture-core');
    SDCBarcode = await import('@scandit/web-datacapture-barcode');
  } catch (e) {
    /**
     * We assume this is due to network issues
     */
    logger.warn('Failed to load Scandit NPM modules', {
      exception: e,
    });
    reportError(e);
    return;
  }

  try {
    const context = await SDCCore.DataCaptureContext.forLicenseKey(SCANDIT_LICENSE_KEY, {
      libraryLocation: new URL(PUBLIC_SDC_LIB_PATH, document.baseURI).toString(),
      moduleLoaders: [SDCBarcode.barcodeCaptureLoader()],
    });

    context.addListener({
      didChangeStatus: (_context: DataCaptureContext, status: ContextStatus) => {
        const { code } = status;
        // https://docs.scandit.com/data-capture-sdk/web/core/api/context-status.html
        if (code !== 1) {
          const error = new NonSuccessScanditStatusError(code, status.message);
          logger.error(error.message, { ...error });
          reportError(error);
        }
      },
    });

    const camera = SDCCore.Camera.pickBestGuessForPosition(SDCCore.CameraPosition.WorldFacing);
    await context.setFrameSource(camera);

    const settings = new SDCBarcode.BarcodeBatchSettings();
    settings.enableSymbologies([SDCBarcode.Symbology.Code128]);

    const barcodeBatch = await SDCBarcode.BarcodeBatch.forContext(context, settings);
    await barcodeBatch.setEnabled(false);

    barcodeBatch.addListener({
      didUpdateSession: (barcodeBatch, session) => {
        const barcodes = Object.entries(session.trackedBarcodes);
        for (const [, value] of barcodes) {
          const barcode = value.barcode.data;
          if (isNil(barcode)) {
            continue;
          }
          onBarcodeDetect(barcode);
        }
      },
    });

    const view = await SDCCore.DataCaptureView.forContext(context);

    view.connectToElement(container);
    view.addControl(new SDCCore.CameraSwitchControl());

    await SDCBarcode.BarcodeBatchBasicOverlay.withBarcodeBatchForView(barcodeBatch, view);

    new SDCCore.RectangularViewfinder(
      SDCCore.RectangularViewfinderStyle.Square,
      SDCCore.RectangularViewfinderLineStyle.Bold,
    );

    try {
      await camera.switchToDesiredState(SDCCore.FrameSourceState.On);
    } catch (e) {
      // Assume that the user has not allowed camera access, more detailed error handling might be required in the future
      logger.debug('Camera is not allowed', { exception: e });
      reportError(e);
      return;
    }
    await barcodeBatch.setEnabled(true);
    onInit();
  } catch (e) {
    logger.error('Unhandled exception while setting up Scanner', {
      exception: e,
    });
    reportError(e);
  }
}
